import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import type { BookingTime, RecurrenceDaysOfWeekEnum } from '../../../api/generated';
import { DEFAULT_INTERVAL_WEEKS } from './bookingConstants';
import {
  buildRecurringOccurrences,
  buildSingleOccurrence,
  firstOccurrenceDate,
  isRecurringTime,
  isSingleOccurrenceTime,
  splitRecurringOccurrence,
  splitSingleOccurrence,
  weekdayOfIsoDate
} from './bookingTime';
import type { RecurrenceFieldErrors } from './BookingRecurrenceFields';

// Base error shape every occurrence-based booking form shares. The index signature lets each form
// add its own entity error (e.g. `patients`, `staff`, `roomId`) without a generic parameter.
export type BookingFormErrors = RecurrenceFieldErrors & {
  date?: string;
  time?: string;
  [key: string]: string | undefined;
};

// The minimal booking shape the form state needs to preload an edit.
export interface BookingLike {
  time: BookingTime;
  reason?: string;
}

interface UseBookingFormStateArgs {
  booking: BookingLike | null;
  /** i18n key prefix for validation messages, e.g. `patient-booking`. */
  keyPrefix: string;
  /**
   * Whether this form offers the all-day option. Staff and room bookings do; patient bookings do
   * not, so they keep requiring a start and end time (REH-304).
   */
  canBeAllDay?: boolean;
}

export interface BookingFormState {
  date: string;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
  /** Whether the form should offer the all-day option at all. */
  canBeAllDay: boolean;
  isRecurring: boolean;
  selectedDays: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  recurrenceEndDate: string;
  exceptionDates: string[];
  reason: string;
  errors: BookingFormErrors;
  firstOccurrence: string | null;
  setDate: Dispatch<SetStateAction<string>>;
  setStartTime: Dispatch<SetStateAction<string>>;
  setEndTime: Dispatch<SetStateAction<string>>;
  setSelectedDays: Dispatch<SetStateAction<RecurrenceDaysOfWeekEnum[]>>;
  setIntervalWeeks: Dispatch<SetStateAction<number>>;
  setRecurrenceEndDate: Dispatch<SetStateAction<string>>;
  setExceptionDates: Dispatch<SetStateAction<string[]>>;
  setReason: Dispatch<SetStateAction<string>>;
  handleToggleRecurring: () => void;
  handleToggleAllDay: () => void;
  /** Builds the occurrence/recurrence `time` payload from the current fields. */
  buildTime: () => BookingTime;
  /**
   * Runs the shared date/time/recurrence validation, plus any entity-specific checks the caller
   * layers in, stores the result, and reports whether the form is valid.
   */
  validate: (applyEntityErrors?: (errors: BookingFormErrors) => void) => boolean;
}

interface InitialFormState {
  date: string;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
  isRecurring: boolean;
  selectedDays: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  recurrenceEndDate: string;
  exceptionDates: string[];
}

// The recurrence kind is fixed once at creation, so on edit a recurring booking loads its full
// pattern (and skipped dates) while a single booking loads its date/time; the Repeat switch only
// renders when creating.
const buildInitialState = (booking: BookingLike | null): InitialFormState => {
  if (booking && isRecurringTime(booking.time)) {
    const recurrence = splitRecurringOccurrence(booking.time);
    return {
      date: recurrence.date,
      startTime: recurrence.startTime,
      endTime: recurrence.endTime,
      isAllDay: recurrence.isAllDay,
      isRecurring: true,
      selectedDays: recurrence.daysOfWeek,
      intervalWeeks: recurrence.intervalWeeks,
      recurrenceEndDate: recurrence.endDate,
      exceptionDates: recurrence.exceptionDates
    };
  }
  const single =
    booking && isSingleOccurrenceTime(booking.time)
      ? splitSingleOccurrence(booking.time)
      : { date: '', startTime: '', endTime: '', isAllDay: false };
  return {
    ...single,
    isRecurring: false,
    selectedDays: [],
    intervalWeeks: DEFAULT_INTERVAL_WEEKS,
    recurrenceEndDate: '',
    exceptionDates: []
  };
};

// Owns the date/time/recurrence/reason state and validation shared by the occurrence-based booking
// forms (patient, staff, room). Each form keeps only its entity picker and payload assembly.
export const useBookingFormState = ({
  booking,
  keyPrefix,
  canBeAllDay = false
}: UseBookingFormStateArgs): BookingFormState => {
  const { t } = useTranslation();
  const initial = buildInitialState(booking);
  const [date, setDate] = useState(initial.date);
  const [startTime, setStartTime] = useState(initial.startTime);
  const [endTime, setEndTime] = useState(initial.endTime);
  // A form without the control must never start in a state it cannot leave, so it loads a stored
  // whole-day booking as its literal window instead — which is exactly how it behaved before the
  // all-day option existed.
  const isLoadedAllDay = canBeAllDay && initial.isAllDay;
  const [isAllDay, setIsAllDay] = useState(isLoadedAllDay);
  const [isRecurring, setIsRecurring] = useState(initial.isRecurring);
  const [selectedDays, setSelectedDays] = useState<RecurrenceDaysOfWeekEnum[]>(initial.selectedDays);
  const [intervalWeeks, setIntervalWeeks] = useState(initial.intervalWeeks);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(initial.recurrenceEndDate);
  const [exceptionDates, setExceptionDates] = useState<string[]>(initial.exceptionDates);
  const [reason, setReason] = useState(booking?.reason ?? '');
  const [errors, setErrors] = useState<BookingFormErrors>({});

  const handleToggleRecurring = (): void => {
    const next = !isRecurring;
    if (next && selectedDays.length === 0) {
      const weekday = weekdayOfIsoDate(date);
      if (weekday) setSelectedDays([weekday]);
    }
    setIsRecurring(next);
  };

  // Only the flag moves: the time fields keep whatever they held, so toggling off restores the
  // window the user had typed (or the stored one) instead of a guess.
  const handleToggleAllDay = (): void => {
    setIsAllDay((current) => !current);
  };

  const firstOccurrence =
    isRecurring && selectedDays.length > 0
      ? firstOccurrenceDate(date, selectedDays, intervalWeeks, recurrenceEndDate || undefined)
      : null;

  const validateRecurrence = (next: BookingFormErrors): void => {
    if (selectedDays.length === 0) next.days = t(`${keyPrefix}-error-days`);
    if (intervalWeeks < DEFAULT_INTERVAL_WEEKS) next.interval = t(`${keyPrefix}-error-interval`);
    if (!recurrenceEndDate || !date) return;
    if (recurrenceEndDate < date) {
      next.endDate = t(`${keyPrefix}-error-end-date`);
    } else if (!next.days && !next.interval && firstOccurrence === null) {
      next.endDate = t(`${keyPrefix}-error-no-occurrences`);
    }
  };

  const validate = (applyEntityErrors?: (errors: BookingFormErrors) => void): boolean => {
    const next: BookingFormErrors = {};
    applyEntityErrors?.(next);
    if (!date) next.date = t(`${keyPrefix}-error-date`);
    // An all-day booking has no times to check; the date and recurrence rules still apply.
    if (!isAllDay) {
      if (!startTime || !endTime) {
        next.time = t(`${keyPrefix}-error-time-required`);
      } else if (endTime <= startTime) {
        next.time = t(`${keyPrefix}-error-time-range`);
      }
    }
    if (isRecurring) validateRecurrence(next);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildTime = (): BookingTime =>
    isRecurring
      ? buildRecurringOccurrences({
          startDate: date,
          endDate: recurrenceEndDate,
          startTime,
          endTime,
          isAllDay,
          daysOfWeek: selectedDays,
          intervalWeeks,
          exceptionDates
        })
      : buildSingleOccurrence({ date, startTime, endTime, isAllDay });

  return {
    date,
    startTime,
    endTime,
    isAllDay,
    canBeAllDay,
    isRecurring,
    selectedDays,
    intervalWeeks,
    recurrenceEndDate,
    exceptionDates,
    reason,
    errors,
    firstOccurrence,
    setDate,
    setStartTime,
    setEndTime,
    setSelectedDays,
    setIntervalWeeks,
    setRecurrenceEndDate,
    setExceptionDates,
    setReason,
    handleToggleRecurring,
    handleToggleAllDay,
    buildTime,
    validate
  };
};
